import { createId } from "@paralleldrive/cuid2";
import {
  defaultAward,
  defaultCertification,
  defaultEducation,
  defaultExperience,
  defaultInterest,
  defaultLanguage,
  defaultProfile,
  defaultPublication,
  defaultReference,
  defaultResumeData,
  defaultSkill,
  defaultVolunteer,
} from "@reactive-resume/schema";
import type { Json } from "@reactive-resume/utils";
import type { Schema } from "zod";

import type { Parser } from "../interfaces/parser";
import { mergeSummaryAndHighlights, plainOrHtmlToRichParagraphs } from "./rich-html";
import type { JsonResume } from "./schema";
import { jsonResumeSchema } from "./schema";

export * from "./iso-date";
export * from "./rich-html";
export * from "./schema";

function joinDateRange(start: string | undefined, end: string | undefined): string {
  const parts = [start, end].filter((d): d is string => typeof d === "string" && d.trim() !== "");
  return parts.join(" - ");
}

function formatEducationInstitution(education: NonNullable<JsonResume["education"]>[number]): string {
  const institution = education.institution?.trim();
  if (institution) {
    return institution;
  }

  const area = education.area?.trim();
  const studyType = education.studyType?.trim();
  if (studyType && area) {
    return `${studyType} in ${area}`;
  }

  return studyType || area || "Education";
}

/** ResumeData stores a single location string; JSON Resume often uses city/region/countryCode only. */
function formatBasicsLocation(
  location: NonNullable<NonNullable<JsonResume["basics"]>["location"]> | undefined,
): string {
  if (!location) {
    return "";
  }
  const address = location.address?.trim();
  if (address) {
    return address;
  }
  const parts: string[] = [];
  for (const p of [
    location.city?.trim(),
    location.region?.trim(),
    location.postalCode?.trim(),
    location.countryCode?.trim(),
  ]) {
    if (p) {
      parts.push(p);
    }
  }
  return parts.join(", ");
}

export class JsonResumeParser implements Parser<Json, JsonResume> {
  schema: Schema;

  constructor() {
    this.schema = jsonResumeSchema;
  }

  readFile(file: File): Promise<Json> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      // eslint-disable-next-line unicorn/prefer-add-event-listener
      reader.onload = () => {
        try {
          const result = JSON.parse(reader.result as string) as Json;
          resolve(result);
        } catch {
          reject(new Error("Failed to parse JSON"));
        }
      };

      // eslint-disable-next-line unicorn/prefer-add-event-listener
      reader.onerror = () => {
        reject(new Error("Failed to read the file"));
      };

      // eslint-disable-next-line unicorn/prefer-blob-reading-methods
      reader.readAsText(file);
    });
  }

  validate(data: Json) {
    return this.schema.parse(data) as JsonResume;
  }

  convert(data: JsonResume) {
    const result = JSON.parse(JSON.stringify(defaultResumeData));

    // Basics
    result.basics.name = data.basics?.name ?? "";
    result.basics.headline = data.basics?.label ?? "";
    result.basics.picture.url = data.basics?.image ?? "";
    result.basics.email = data.basics?.email ?? "";
    result.basics.phone = data.basics?.phone ?? "";
    result.basics.location = formatBasicsLocation(data.basics?.location);
    result.basics.url.href = data.basics?.url ?? "";
    result.sections.summary.content = plainOrHtmlToRichParagraphs(data.basics?.summary ?? "");

    // Profiles: schema requires network + username; include network-only rows (PDFs often list platforms without handles).
    if (data.basics?.profiles) {
      for (const profile of data.basics.profiles) {
        const networkRaw = profile.network?.trim() ?? "";
        const username = profile.username?.trim() ?? "";
        const href = (profile.url ?? "").trim();
        if (!networkRaw && !username && !href) {
          continue;
        }
        const slug = (networkRaw || username || "link").toLowerCase();
        const networkSlug = networkRaw ? networkRaw.toLowerCase() : username.toLowerCase();
        result.sections.profiles.items.push({
          ...defaultProfile,
          id: createId(),
          icon: slug,
          network: networkSlug,
          username: username || networkRaw || networkSlug,
          url: { ...defaultProfile.url, href },
        });
      }
    }

    // Work
    if (data.work) {
      for (const work of data.work) {
        result.sections.experience.items.push({
          ...defaultExperience,
          id: createId(),
          company: work.name ?? "",
          position: work.position ?? "",
          summary: mergeSummaryAndHighlights(work.summary, work.highlights),
          date: joinDateRange(work.startDate, work.endDate),
          url: { ...defaultExperience.url, href: work.url ?? "" },
        });
      }
    }

    // Volunteer
    if (data.volunteer) {
      for (const volunteer of data.volunteer) {
        result.sections.volunteer.items.push({
          ...defaultVolunteer,
          id: createId(),
          organization: volunteer.organization ?? "",
          date: joinDateRange(volunteer.startDate, volunteer.endDate),
          position: volunteer.position ?? "",
          summary: mergeSummaryAndHighlights(volunteer.summary, volunteer.highlights),
          url: { ...defaultVolunteer.url, href: volunteer.url ?? "" },
        });
      }
    }

    // Education
    if (data.education) {
      for (const education of data.education) {
        result.sections.education.items.push({
          ...defaultEducation,
          id: createId(),
          institution: formatEducationInstitution(education),
          studyType: education.studyType ?? "",
          area: education.area ?? "",
          score: education.score ?? "",
          date: joinDateRange(education.startDate, education.endDate),
          url: { ...defaultEducation.url, href: education.url ?? "" },
        });
      }
    }

    // Awards
    if (data.awards) {
      for (const award of data.awards) {
        result.sections.awards.items.push({
          ...defaultAward,
          id: createId(),
          title: award.title ?? "",
          date: award.date ?? "",
          awarder: award.awarder ?? "",
          summary: award.summary ?? "",
        });
      }
    }

    // Certificates
    if (data.certificates) {
      for (const certificate of data.certificates) {
        result.sections.certifications.items.push({
          ...defaultCertification,
          id: createId(),
          name: certificate.name ?? "",
          date: certificate.date ?? "",
          issuer: certificate.issuer ?? "",
          summary: certificate.summary ?? "",
        });
      }
    }

    // Publications
    if (data.publications) {
      for (const publication of data.publications) {
        result.sections.publications.items.push({
          ...defaultPublication,
          id: createId(),
          name: publication.name ?? "",
          publisher: publication.publisher ?? "",
          summary: publication.summary ?? "",
          date: publication.releaseDate ?? "",
          url: { ...defaultPublication.url, href: publication.url ?? "" },
        });
      }
    }

    // Skills
    if (data.skills) {
      for (const skill of data.skills) {
        result.sections.skills.items.push({
          ...defaultSkill,
          id: createId(),
          name: skill.name ?? "",
          description: skill.level ?? "",
          keywords: skill.keywords ?? [],
        });
      }
    }

    // Languages
    if (data.languages) {
      for (const language of data.languages) {
        result.sections.languages.items.push({
          ...defaultLanguage,
          id: createId(),
          name: language.language ?? "",
          description: language.fluency ?? "",
        });
      }
    }

    // Interests
    if (data.interests) {
      for (const interest of data.interests) {
        result.sections.interests.items.push({
          ...defaultInterest,
          id: createId(),
          name: interest.name ?? "",
          keywords: interest.keywords ?? [],
        });
      }
    }

    // References
    if (data.references) {
      for (const reference of data.references) {
        result.sections.references.items.push({
          ...defaultReference,
          id: createId(),
          name: reference.name ?? "",
          summary: reference.reference ?? "",
        });
      }
    }

    return result;
  }
}
